import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  MapPin, Plus, Pencil, Trash2, Loader2, Search, RefreshCw, ShieldAlert,
  CheckCircle2, Building2,
} from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  outletName: '', franchiseeName: '', city: '', state: '', district: '',
  pinCode: '', status: 'Active', openedDate: '', notes: '',
};

const FranchiseOutletsPage = () => {
  const { api, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Add/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Inline territory check
  const [checkPin, setCheckPin] = useState('');
  const [checkCity, setCheckCity] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const canDelete = user && (user.role === 'Super Admin' || user.role === 'CEO');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/franchise-outlets/');
      setItems(data.items || []);
    } catch (_e) {
      toast.error('Failed to load outlets');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      outletName: item.outletName || '',
      franchiseeName: item.franchiseeName || '',
      city: item.city || '',
      state: item.state || '',
      district: item.district || '',
      pinCode: item.pinCode || '',
      status: item.status || 'Active',
      openedDate: item.openedDate || '',
      notes: item.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.outletName.trim() || !form.city.trim()) {
      return toast.error('Outlet name and city are required');
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/franchise-outlets/${editing.id}`, form);
        toast.success('Outlet updated');
      } else {
        await api.post('/franchise-outlets/', form);
        toast.success('Outlet added');
      }
      setDialogOpen(false);
      await refresh();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/franchise-outlets/${deleteTarget.id}`);
      toast.success(`Outlet "${deleteTarget.outletName}" deleted`);
      setDeleteTarget(null);
      await refresh();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Delete failed';
      toast.error(typeof msg === 'string' ? msg : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const runCheck = async () => {
    if (!checkPin && !checkCity) return toast.error('Enter pincode or city');
    setChecking(true);
    setCheckResult(null);
    try {
      const params = new URLSearchParams();
      if (checkPin) params.set('pinCode', checkPin);
      if (checkCity) params.set('city', checkCity);
      const { data } = await api.get(`/franchise-outlets/check?${params}`);
      setCheckResult(data);
    } catch (_e) {
      toast.error('Check failed');
    } finally {
      setChecking(false);
    }
  };

  const filtered = items.filter((it) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [
      it.outletName, it.franchiseeName, it.city, it.state, it.pinCode,
    ].some((v) => (v || '').toLowerCase().includes(q));
  });

  const stats = {
    total: items.length,
    active: items.filter((i) => i.status === 'Active').length,
    upcoming: items.filter((i) => i.status === 'Upcoming').length,
    closed: items.filter((i) => i.status === 'Closed').length,
  };

  return (
    <div className="space-y-6" data-testid="franchise-outlets-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Building2 className="h-7 w-7 text-emerald-600" /> Franchise Outlets Registry
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">
            Master list of allotted Servall outlets. Used by the Territory Checker
            to flag exclusive pincodes before pitching a new franchise.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={refresh}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={openAdd} data-testid="add-outlet-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Outlet
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><div className="text-xs uppercase opacity-70">Total</div><div className="text-2xl font-bold">{stats.total}</div></CardContent></Card>
        <Card><CardContent className="p-4 bg-emerald-50/40"><div className="text-xs uppercase opacity-70 text-emerald-700">Active</div><div className="text-2xl font-bold text-emerald-800">{stats.active}</div></CardContent></Card>
        <Card><CardContent className="p-4 bg-amber-50/40"><div className="text-xs uppercase opacity-70 text-amber-700">Upcoming</div><div className="text-2xl font-bold text-amber-800">{stats.upcoming}</div></CardContent></Card>
        <Card><CardContent className="p-4 bg-zinc-50"><div className="text-xs uppercase opacity-70 text-zinc-600">Closed</div><div className="text-2xl font-bold text-zinc-700">{stats.closed}</div></CardContent></Card>
      </div>

      {/* Territory Checker */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Search className="h-5 w-5" /> Quick Territory Check</CardTitle>
          <CardDescription>Type a pincode or city to see if it&apos;s already taken.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={checkPin} placeholder="Pincode (6 digits)"
              onChange={(e) => setCheckPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="sm:max-w-[180px]" />
            <Input value={checkCity} placeholder="City"
              onChange={(e) => setCheckCity(e.target.value)} />
            <Button onClick={runCheck} disabled={checking}>
              {checking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Check
            </Button>
          </div>
          {checkResult && (
            checkResult.available
              ? <Alert className="border-emerald-300 bg-emerald-50"><CheckCircle2 className="h-4 w-4 text-emerald-700" /><AlertDescription className="text-emerald-800">🟢 Open! No active Servall outlet exists here.</AlertDescription></Alert>
              : <Alert variant="destructive"><ShieldAlert className="h-4 w-4" /><AlertDescription>🔴 Allocated. {checkResult.conflicts.map((c) => `${c.outletName}${c.pinCode ? ` (${c.pinCode})` : ''}`).join(', ')}</AlertDescription></Alert>
          )}
        </CardContent>
      </Card>

      {/* Outlets list */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle>Connected Outlets ({filtered.length})</CardTitle>
            <Input placeholder="Search by name, city, pincode..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="sm:max-w-sm" />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <Alert><MapPin className="h-4 w-4" /><AlertDescription>No outlets yet. Click "Add Outlet" to register the first one.</AlertDescription></Alert>
          ) : (
            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Outlet</TableHead>
                    <TableHead>Franchisee</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Pin</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((it) => (
                    <TableRow key={it.id} data-testid={`outlet-row-${it.id}`}>
                      <TableCell className="font-medium">{it.outletName}</TableCell>
                      <TableCell>{it.franchiseeName || '—'}</TableCell>
                      <TableCell>{it.city}{it.state && <span className="text-xs text-muted-foreground">, {it.state}</span>}</TableCell>
                      <TableCell className="font-mono text-xs">{it.pinCode || '—'}</TableCell>
                      <TableCell>
                        <Badge variant={it.status === 'Active' ? 'default' : it.status === 'Upcoming' ? 'secondary' : 'outline'}
                          className={it.status === 'Active' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : ''}>
                          {it.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(it)}><Pencil className="h-3.5 w-3.5" /></Button>
                        {canDelete && (
                          <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(it)} className="text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="outlet-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Outlet' : 'Add Franchise Outlet'}</DialogTitle>
            <DialogDescription>{editing ? 'Update outlet details.' : 'Register a new allotted franchise.'}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2">
              <Label>Outlet Name *</Label>
              <Input value={form.outletName} onChange={(e) => setForm({ ...form, outletName: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Franchisee Name</Label>
              <Input value={form.franchiseeName} onChange={(e) => setForm({ ...form, franchiseeName: e.target.value })} />
            </div>
            <div>
              <Label>City *</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div>
              <Label>State</Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </div>
            <div>
              <Label>District</Label>
              <Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} />
            </div>
            <div>
              <Label>Pincode (6 digits)</Label>
              <Input value={form.pinCode} onChange={(e) => setForm({ ...form, pinCode: e.target.value.replace(/\D/g, '').slice(0, 6) })} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Upcoming">Upcoming</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Opened Date</Label>
              <Input type="date" value={form.openedDate} onChange={(e) => setForm({ ...form, openedDate: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-outlet-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              {editing ? 'Save Changes' : 'Add Outlet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete "{deleteTarget?.outletName}"?</DialogTitle>
            <DialogDescription>
              This removes the outlet from the franchise registry. Lead records and CRM data are untouched.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FranchiseOutletsPage;
